import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Server, Copy, RotateCw, Trash2, Users, BrushCleaning, Save, Eye, EyeOff } from "lucide-react";
import { Section } from "./Section";

export function AgentSection({ s, setS, agentStatus, agentToken, rotateAgentToken, revokeAgentToken, saveAgentToken, onRefresh }) {
  const { t } = useLang();
  const [manualToken, setManualToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const clearErrors = async () => {
    try { const { data } = await api.post("/agent/errors/clear"); toast.success(`${t("agent.errorsCleared")} (${data.cleared})`); onRefresh?.(); }
    catch { toast.error(t("agent.errorsClearFail")); }
  };
  const submitManual = async () => {
    const ok = await saveAgentToken?.(manualToken.trim());
    if (ok) setManualToken("");
  };
  return (
    <Section icon={Server} title={t("agent.title")}>
      <p className="mb-4 text-xs text-zinc-400">{t("agent.intro")}</p>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-bold uppercase tracking-wider ${agentStatus?.connected ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`} data-testid="agent-conn-status">
            <span className={`h-2 w-2 rounded-full ${agentStatus?.connected ? "bg-emerald-500" : "bg-zinc-400"}`} />
            {agentStatus?.connected ? t("agent.connected") : t("agent.disconnected")}
          </span>
          <span className="text-xs text-zinc-400">{t("agent.lastSeen")}: {agentStatus?.lastSeen ? new Date(agentStatus.lastSeen).toLocaleString() : t("agent.never")}{agentStatus?.lastAgentId ? ` · ${agentStatus.lastAgentId}` : ""}</span>
        </div>

        <div className="grid grid-cols-3 gap-px overflow-hidden rounded-sm border border-zinc-200 bg-zinc-200">
          {[["queued", t("agent.queued"), agentStatus?.queue?.queued ?? 0, "text-zinc-900"], ["claimed", t("agent.claimed"), agentStatus?.queue?.claimed ?? 0, "text-sky-700"], ["error", t("agent.errors"), agentStatus?.queue?.error ?? 0, "text-red-600"]].map(([key, lbl, val, cls]) => (
            <div key={key} className="bg-white p-3 text-center">
              <div className={`font-display text-2xl font-black ${cls}`} data-testid={`agent-q-${key}`}>{val}</div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{lbl}</div>
            </div>
          ))}
        </div>
        {(agentStatus?.queue?.error ?? 0) > 0 && (
          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={clearErrors} data-testid="agent-clear-errors" className="rounded-sm border-zinc-300 text-zinc-600 hover:bg-zinc-50">
              <BrushCleaning size={15} className="mr-1.5" /> {t("agent.clearErrors")}
            </Button>
          </div>
        )}

        <div className="space-y-2 rounded-sm border border-zinc-200 bg-zinc-50 p-4">
          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("agent.token")}</Label>
          {agentToken ? (
            <>
              <div className="flex gap-2">
                <Input readOnly value={agentToken} data-testid="agent-token-value" className="rounded-sm border-emerald-300 bg-white font-mono text-xs" />
                <Button type="button" variant="outline" onClick={() => { navigator.clipboard.writeText(agentToken); toast.success(t("agent.copied")); }} className="shrink-0 rounded-sm border-zinc-300"><Copy size={15} className="mr-1.5" /> {t("agent.copy")}</Button>
              </div>
              <p className="text-[11px] font-semibold text-amber-700">{t("agent.tokenOnce")}</p>
            </>
          ) : (
            <p className="text-[11px] text-zinc-500">{agentStatus?.tokenSet ? t("agent.tokenSet") : t("agent.noToken")}</p>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button type="button" variant="outline" onClick={rotateAgentToken} data-testid="agent-token-rotate" className="rounded-sm border-zinc-300">
              <RotateCw size={15} className="mr-1.5" /> {agentStatus?.tokenSet ? t("agent.rotate") : t("agent.generate")}
            </Button>
            {agentStatus?.tokenSet && (
              <Button type="button" variant="outline" onClick={revokeAgentToken} data-testid="agent-token-revoke" className="rounded-sm border-red-300 text-red-600 hover:bg-red-50">
                <Trash2 size={15} className="mr-1.5" /> {t("agent.revoke")}
              </Button>
            )}
          </div>
          <div className="space-y-1.5 border-t border-zinc-200 pt-3">
            <Label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">{t("agent.tokenManual")}</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input type={showToken ? "text" : "password"} value={manualToken} onChange={(e) => setManualToken(e.target.value)} placeholder={t("agent.tokenManualPlaceholder")}
                  autoComplete="off" data-testid="agent-token-manual-input" className="rounded-sm border-zinc-300 pr-9 font-mono text-xs" />
                <button type="button" onClick={() => setShowToken((v) => !v)} data-testid="agent-token-manual-toggle"
                  aria-label={showToken ? t("agent.tokenHide") : t("agent.tokenShow")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
                  {showToken ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <Button type="button" variant="outline" onClick={submitManual} disabled={manualToken.trim().length < 16}
                data-testid="agent-token-manual-save" className="shrink-0 rounded-sm border-zinc-300">
                <Save size={15} className="mr-1.5" /> {t("agent.tokenSave")}
              </Button>
            </div>
            <p className="text-[11px] text-zinc-400">{t("agent.tokenManualHint")}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.pollEvery")}</Label>
            <Input type="number" min={30} max={3600} value={s.pollSeconds || 300} onChange={(e) => setS({ ...s, pollSeconds: Number(e.target.value) })} data-testid="settings-poll-seconds" className="rounded-sm border-zinc-300" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.syncWaitMax")}</Label>
            <Input type="number" min={1} max={100} value={s.verifyMaxAttempts || 10} onChange={(e) => setS({ ...s, verifyMaxAttempts: Number(e.target.value) })} data-testid="settings-verify-attempts" className="rounded-sm border-zinc-300" />
          </div>
        </div>
        <p className="text-[11px] text-zinc-400">{t("agent.spec")}</p>
      </div>
    </Section>
  );
}
